import * as React from "react";
import { cn } from "@/lib/utils";

export interface RevealProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stagger delay in milliseconds. */
  delay?: number;
}

/**
 * Scroll reveal that FAILS OPEN.
 *
 * The server renders no `data-reveal` attribute at all, so SSR HTML never
 * contains `opacity: 0`. After hydration, the pending (hidden) state is applied
 * only to elements that are not yet in the viewport, and a 1500ms safety timer
 * forces anything still pending to become visible. If JS never runs, hydration
 * fails, or the observer never fires, the content is simply visible.
 * Do not combine this component with the CSS-only `laa-rise` hero entrance.
 */
export function Reveal({ delay = 0, className, children, ...props }: RevealProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [state, setState] = React.useState<"idle" | "pending" | "visible">("idle");

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") return;

    // Late hydration means the page has already been on screen for a while.
    // Hiding content at that point is a regression, not polish — skip the
    // animation entirely and leave everything visible.
    if (typeof performance !== "undefined" && performance.now() > 1200) return;

    // Already in view at hydration: leave it visible, never hide it.
    const rect = el.getBoundingClientRect();
    const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
    if (inViewport) return;


    setState("pending");

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setState("visible");
            observer.disconnect();
          }
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);

    // Safety net: never let the observer be a single point of failure.
    const timer = window.setTimeout(() => {
      setState((prev) => (prev === "pending" ? "visible" : prev));
      observer.disconnect();
    }, 900);

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      {...(state === "idle" ? {} : { "data-reveal": state })}
      className={cn(className)}
      style={{ animationDelay: delay ? `${delay}ms` : undefined }}
      {...props}
    >
      {children}
    </div>
  );
}

export default Reveal;
